import React from "react";
import clsx from "clsx";
import {
    ChevronRight,
    Clock,
    Mail,
    MessageSquare,
    Phone,
    ShieldCheck,
    Zap,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { ContactDetails } from "../../../types/business-content.types";
import { buildSmsHref, buildTelHref } from "../../../lib/utils/phone";

export type ContactUsBlockContactDetails = ContactDetails & {
    contactEmailTwo?: string;
    contactEmailSecondary?: string;
    secondaryContactEmail?: string;
};

export type ContactUsBlockLabels = {
    phoneHeading?: string;
    smsHeading?: string;
    emailHeading?: string;
    secondaryLabel?: string;
    phoneNote?: React.ReactNode;
    smsNote?: React.ReactNode;
    emailNote?: React.ReactNode;
};

export type ContactUsBlockProps = {
    contactDetails: ContactUsBlockContactDetails;
    extraTileClasses?: string;
    hideSMS?: boolean;
    title?: React.ReactNode;
    subtitle?: React.ReactNode;
    subTitle?: React.ReactNode;
    showHeader?: boolean;
    secondaryEmail?: string;
    labels?: ContactUsBlockLabels;
    className?: string;
    headerClassName?: string;
    panelClassName?: string;
};

type ContactCard = {
    id: "phone" | "sms" | "email";
    icon: LucideIcon;
    heading: string;
    value?: string;
    additionalValue?: string;
    href: (value: string) => string;
    noteIcon: LucideIcon;
    note: React.ReactNode;
};

const defaultLabels: Required<ContactUsBlockLabels> = {
    phoneHeading: "Phone",
    smsHeading: "SMS",
    emailHeading: "Email",
    secondaryLabel: "Secondary",
    phoneNote: "Mon-Fri 8:00am-6:00pm",
    smsNote: "Fast response during business hours",
    emailNote: "Replies within 1 business day",
};

const buildPhoneLinkHref = (value: string) => buildTelHref(value) || `tel:${value.trim()}`;

const buildSmsLinkHref = (value: string) => buildSmsHref(value) || `sms:${value.trim()}`;

const buildEmailHref = (value: string) => `mailto:${value.trim()}`;

const getSecondaryEmail = (
    contactDetails: ContactUsBlockContactDetails,
    secondaryEmail?: string
) =>
    secondaryEmail ||
    contactDetails.contactEmailTwo ||
    contactDetails.contactEmailSecondary ||
    contactDetails.secondaryContactEmail;

const ContactActionLink = ({
    href,
    value,
    size = "primary",
}: {
    href: string;
    value: string;
    size?: "primary" | "secondary";
}) => (
    <a
        href={href}
        className={clsx(
            "group/link inline-flex min-w-0 max-w-full items-center justify-center gap-2 font-semibold text-blue-600 transition hover:text-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2",
            size === "primary" ? "text-xl sm:text-2xl" : "text-base sm:text-lg"
        )}
    >
        <span className="min-w-0 break-words">{value}</span>
        <ChevronRight className="h-5 w-5 flex-none transition group-hover/link:translate-x-0.5" />
    </a>
);

const ContactMethodCard = ({
    method,
    secondaryLabel,
    extraTileClasses,
}: {
    method: ContactCard;
    secondaryLabel: string;
    extraTileClasses?: string;
}) => {
    const Icon = method.icon;
    const NoteIcon = method.noteIcon;

    return (
        <article
            className={clsx(
                "flex h-full min-h-[18rem] min-w-0 flex-col items-center rounded-lg border border-slate-200 bg-white px-5 py-7 text-center shadow-[0_16px_42px_rgba(15,23,42,0.08)] sm:px-7 sm:py-8",
                extraTileClasses
            )}
        >
            <div className="mb-5 grid h-16 w-16 place-items-center rounded-full bg-blue-50 text-blue-600 ring-1 ring-blue-100">
                <Icon className="h-8 w-8" strokeWidth={2.25} />
            </div>

            <h3 className="text-xl font-bold leading-tight text-slate-950">
                {method.heading}
            </h3>

            <div className="mt-5 flex min-w-0 flex-col items-center">
                {method.value ? (
                    <ContactActionLink
                        href={method.href(method.value)}
                        value={method.value}
                    />
                ) : null}
            </div>

            {method.additionalValue ? (
                <div className="mt-5 w-full border-t border-slate-200 pt-5">
                    <div className="text-sm font-medium text-slate-500">
                        {secondaryLabel}
                    </div>
                    <div className="mt-1 flex min-w-0 flex-col items-center">
                        <ContactActionLink
                            href={method.href(method.additionalValue)}
                            value={method.additionalValue}
                            size="secondary"
                        />
                    </div>
                </div>
            ) : null}

            <div className="mt-auto w-full pt-6">
                <div className="flex min-h-12 items-center justify-center gap-3 rounded-lg bg-slate-100 px-4 py-3 text-sm font-medium text-slate-600">
                    <NoteIcon
                        className="h-5 w-5 flex-none text-blue-600"
                        strokeWidth={2}
                    />
                    <span className="min-w-0 break-words text-left">{method.note}</span>
                </div>
            </div>
        </article>
    );
};

const getContactGridClasses = (total: number) =>
    clsx(
        "mx-auto grid w-full grid-cols-1 gap-5",
        total === 1 && "max-w-md",
        total > 1 && "md:grid-cols-4",
        total > 1 && total < 5 && "max-w-4xl xl:grid-cols-4",
        total >= 5 && "max-w-none xl:grid-cols-6"
    );

const getContactTileLayoutClasses = (index: number, total: number) => {
    const supportsThreeColumnRows = total >= 5;
    const isLast = index === total - 1;
    const isSingleTileLastRow = total % 2 === 1 && isLast;
    const isFirstTileInTwoItemLastRow =
        supportsThreeColumnRows && total % 3 === 2 && index === total - 2;
    const isSingleTileInThreeColumnLastRow =
        supportsThreeColumnRows && total % 3 === 1 && isLast;

    return clsx(
        "min-w-0",
        total > 1 && "md:col-span-2",
        isSingleTileLastRow && "md:col-start-2",
        supportsThreeColumnRows && "xl:col-span-2 xl:col-start-auto",
        isFirstTileInTwoItemLastRow && "xl:col-start-2",
        isSingleTileInThreeColumnLastRow && "xl:col-start-3"
    );
};

const ContactUsBlock = ({
    contactDetails,
    extraTileClasses,
    hideSMS,
    title = "Get in touch",
    subtitle,
    subTitle,
    showHeader = true,
    secondaryEmail,
    labels,
    className,
    headerClassName,
    panelClassName,
}: ContactUsBlockProps) => {
    const { contactEmail, contactNumberOne, contactNumberTwo } = contactDetails;
    const resolvedLabels = { ...defaultLabels, ...labels };
    const resolvedSubtitle =
        subtitle ?? subTitle ?? "We're here to help. Choose the way that's easiest for you.";

    const availableContactMethods: ContactCard[] = [
        {
            id: "phone",
            icon: Phone,
            heading: resolvedLabels.phoneHeading,
            href: buildPhoneLinkHref,
            value: contactNumberOne,
            additionalValue: contactNumberTwo,
            noteIcon: Clock,
            note: resolvedLabels.phoneNote,
        },
        {
            id: "sms",
            icon: MessageSquare,
            heading: resolvedLabels.smsHeading,
            href: buildSmsLinkHref,
            value: contactNumberOne,
            additionalValue: contactNumberTwo,
            noteIcon: Zap,
            note: resolvedLabels.smsNote,
        },
        {
            id: "email",
            icon: Mail,
            heading: resolvedLabels.emailHeading,
            href: buildEmailHref,
            value: contactEmail,
            additionalValue: getSecondaryEmail(contactDetails, secondaryEmail),
            noteIcon: ShieldCheck,
            note: resolvedLabels.emailNote,
        },
    ];

    const contactMethods = availableContactMethods.filter(
        (method) => method.value && (method.id !== "sms" || !hideSMS)
    );

    if (!contactMethods.length) return null;

    return (
        <section
            className={clsx(
                "w-full px-4 py-12 text-slate-950 sm:px-6 sm:py-16",
                className
            )}
        >
            <div className="mx-auto w-full max-w-7xl">
                {showHeader ? (
                    <div
                        className={clsx(
                            "mx-auto mb-10 max-w-3xl text-center sm:mb-14",
                            headerClassName
                        )}
                    >
                        {title ? (
                            <h2 className="text-3xl font-bold leading-tight tracking-normal text-slate-950 sm:text-4xl">
                                {title}
                            </h2>
                        ) : null}
                        {resolvedSubtitle ? (
                            <p className="mt-4 text-base font-medium leading-7 text-slate-500 sm:text-lg">
                                {resolvedSubtitle}
                            </p>
                        ) : null}
                    </div>
                ) : null}

                <div
                    className={clsx(
                        "rounded-2xl border border-slate-200/80 bg-white/95 p-4 shadow-[0_22px_70px_rgba(15,23,42,0.10)] sm:p-7 lg:p-9",
                        panelClassName
                    )}
                >
                    <div className={getContactGridClasses(contactMethods.length)}>
                        {contactMethods.map((method, index) => (
                            <div
                                key={method.id}
                                className={getContactTileLayoutClasses(
                                    index,
                                    contactMethods.length
                                )}
                            >
                                <ContactMethodCard
                                    method={method}
                                    secondaryLabel={resolvedLabels.secondaryLabel}
                                    extraTileClasses={extraTileClasses}
                                />
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default ContactUsBlock;
